import {
  AssignmentBodyItem,
  AssignmentPage,
  PageLikeType,
  QuestionBodyItem
} from 'interfaces/assignments'

const QUESTION_CHOICE_KEYS = [
  { code: 'A', key: 'choice_1' },
  { code: 'B', key: 'choice_2' },
  { code: 'C', key: 'choice_3' },
  { code: 'D', key: 'choice_4' }
] as const

type QuestionChoiceKey = (typeof QUESTION_CHOICE_KEYS)[number]['key']

export type QuestionChoiceOption = {
  code: (typeof QUESTION_CHOICE_KEYS)[number]['code']
  key: QuestionChoiceKey
  value: string
  answerValue: number
}

/**
 * Builds choice options with zero-based answer mapping:
 * 0 => choice_1, 1 => choice_2, 2 => choice_3, 3 => choice_4.
 */
export const getQuestionChoiceOptions = (
  choices?: Partial<Record<QuestionChoiceKey, string>>
): QuestionChoiceOption[] => {
  return QUESTION_CHOICE_KEYS.map((choiceMeta, index) => ({
    ...choiceMeta,
    value: choices?.[choiceMeta.key] || '',
    answerValue: index
  }))
}

/**
 * Common "page-like" shape used across V2 CMS helpers.
 *
 * Why this union exists:
 * - Some callers pass the full page object.
 * - Some callers pass only a page type string.
 * - Some call sites provide partial objects (e.g. `{ page_type }`).
 * - Some values can be null while data is loading.
 *
 * These helpers normalize all of those input forms.
 */
type PageLike = AssignmentPage | null | string | { page_type?: PageLikeType }

/**
 * Safely resolves a page type from any supported PageLike value.
 *
 * Returns:
 * - `undefined` when there is no page value.
 * - the string itself when caller already passed a type literal.
 * - `page.page_type` when caller passed an object.
 */
const getPageType = (page?: PageLike) => {
  // Loading or empty state: nothing to resolve.
  if (page == null) {
    return undefined
  }

  // Allow helpers to accept direct page-type strings.
  if (typeof page === 'string') {
    return page
  }

  // Default object path.
  return page.page_type
}

/**
 * True when the value resolves to a section page.
 */
export const isSectionPage = (page?: PageLike) => {
  return getPageType(page) === PageLikeType.SECTION
}

/**
 * True when the value resolves to a question-set page.
 */
export const isQuestionSetPage = (page?: PageLike) => {
  return getPageType(page) === PageLikeType.QUESTION_SET
}

/**
 * True when the value resolves to a teacher-guide section page.
 */
export const isTeacherGuidePage = (page?: PageLike) => {
  return getPageType(page) === PageLikeType.TEACHER_GUIDE_SECTION
}

/**
 * Expands page index strings into individual page numbers.
 *
 * Examples:
 * - "1-5" => ["1", "2", "3", "4", "5"]
 * - "2-8, 10" => ["2", "3", "4", "5", "6", "7", "8", "10"]
 * - "4" => ["4"]
 *
 * Behavior:
 * - Supports both hyphen (-) and en dash (–) for ranges.
 * - Ignores invalid segments without throwing.
 */
export const expandPageIndexRange = (pageIndex?: string): string[] => {
  // Guard empty input so callers can pass optional values safely.
  if (!pageIndex) {
    return []
  }

  // Split comma-separated segments and expand each one independently.
  return pageIndex.split(',').flatMap((segment) => {
    const cleanedSegment = segment.trim()

    // Ignore empty comma segments like ", ,".
    if (!cleanedSegment) {
      return []
    }

    // Matches ranges such as "1-5" or "1 – 5".
    const rangeMatch = cleanedSegment.match(/^(\d+)\s*[-–]\s*(\d+)$/)
    if (rangeMatch) {
      const start = Number(rangeMatch[1])
      const end = Number(rangeMatch[2])

      if (!Number.isFinite(start) || !Number.isFinite(end)) {
        return []
      }

      // Normalize reversed ranges like "8-2" into ascending order.
      const min = Math.min(start, end)
      const max = Math.max(start, end)

      return Array.from({ length: max - min + 1 }, (_, idx) =>
        String(min + idx)
      )
    }

    // Accept single numeric pages like "7".
    return /^\d+$/.test(cleanedSegment) ? [cleanedSegment] : []
  })
}

/**
 * Narrowing helper for mixed body-item arrays.
 *
 * Body items on assignment pages can include text/content/question blocks.
 * We use this guard before accessing question-specific fields.
 */
const isQuestionBodyItem = (
  item: AssignmentBodyItem | QuestionBodyItem
): item is QuestionBodyItem => {
  return item.body_item_type === 'question' && 'question_text' in item
}

/**
 * Builds a stable snapshot of question-only data from a page.
 *
 * We intentionally include only fields that impact grading behavior:
 * - question text
 * - selected answer
 * - four choices
 * - order/index and id for deterministic ordering
 */
const toQuestionSnapshot = (page?: AssignmentPage | null) => {
  // No page means no questions.
  if (!page) {
    return []
  }

  // Explicitly treat body items as mixed types, then narrow safely.
  const mixedBodyItems = page.body_items as (
    | AssignmentBodyItem
    | QuestionBodyItem
  )[]

  // Keep only grading-relevant question data in a deterministic order.
  return mixedBodyItems
    .filter(
      (item: AssignmentBodyItem | QuestionBodyItem): item is QuestionBodyItem =>
        isQuestionBodyItem(item)
    )
    .map((item: QuestionBodyItem) => ({
      id: item.id,
      order_index: item.order_index,
      question_text: item.question_text || '',
      answer: item.answer || 0,
      choices: {
        choice_1: item.choices?.choice_1 || '',
        choice_2: item.choices?.choice_2 || '',
        choice_3: item.choices?.choice_3 || '',
        choice_4: item.choices?.choice_4 || ''
      }
    }))
    .sort((a, b) => a.order_index - b.order_index)
}

/**
 * Compares grading-relevant question content between two page versions.
 *
 * Returns true when questions were edited, added, removed, re-ordered,
 * or when answers/choices/question text changed.
 */
export const hasQuestionChanges = (
  currentPage?: AssignmentPage | null,
  originalPage?: AssignmentPage | null
) => {
  // Snapshot comparison is enough because snapshot contents are:
  // - normalized
  // - sorted by order_index
  // - limited to grading-relevant keys only
  return (
    JSON.stringify(toQuestionSnapshot(currentPage)) !==
    JSON.stringify(toQuestionSnapshot(originalPage))
  )
}

/**
 * Produces an id-keyed view of question data.
 *
 * Why this separate shape exists:
 * - `hasQuestionChanges` answers only yes/no.
 * - graded warning modal also needs metadata (count + answer impact).
 * - id-based map makes add/remove/change detection straightforward.
 */
const toQuestionSnapshotById = (page?: AssignmentPage | null) => {
  const snapshot = new Map<
    number,
    {
      answer: number
      questionText: string
      choices: {
        choice_1: string
        choice_2: string
        choice_3: string
        choice_4: string
      }
    }
  >()

  // No page means empty map.
  if (!page) {
    return snapshot
  }

  // Build normalized entries keyed by question id.
  ;(page.body_items as (AssignmentBodyItem | QuestionBodyItem)[])
    .filter((item): item is QuestionBodyItem => isQuestionBodyItem(item))
    .forEach((item) => {
      snapshot.set(item.id, {
        answer: item.answer || 0,
        questionText: item.question_text || '',
        choices: {
          choice_1: item.choices?.choice_1 || '',
          choice_2: item.choices?.choice_2 || '',
          choice_3: item.choices?.choice_3 || '',
          choice_4: item.choices?.choice_4 || ''
        }
      })
    })

  return snapshot
}

/**
 * Returns warning modal metadata for graded assignments.
 *
 * - `updatedQuestions`: number of changed/added/removed questions.
 * - `answerUpdated`: true when at least one answer key changed.
 */
export const getQuestionChangeMeta = (
  currentPage?: AssignmentPage | null,
  originalPage?: AssignmentPage | null
) => {
  // Convert both versions into consistent id-keyed maps.
  const currentById = toQuestionSnapshotById(currentPage)
  const originalById = toQuestionSnapshotById(originalPage)

  // Union all question ids to catch edits and add/remove operations.
  const allIds = new Set<number>([
    ...Array.from(currentById.keys()),
    ...Array.from(originalById.keys())
  ])

  // Values consumed by graded-assignment warning modal.
  let updatedQuestions = 0
  let answerUpdated = false

  allIds.forEach((questionId) => {
    const currentQuestion = currentById.get(questionId)
    const originalQuestion = originalById.get(questionId)

    // Missing on either side means added or removed question.
    if (!currentQuestion || !originalQuestion) {
      updatedQuestions += 1
      return
    }

    // Detect any grading-relevant content change for this question.
    const questionChanged =
      currentQuestion.questionText !== originalQuestion.questionText ||
      currentQuestion.choices.choice_1 !== originalQuestion.choices.choice_1 ||
      currentQuestion.choices.choice_2 !== originalQuestion.choices.choice_2 ||
      currentQuestion.choices.choice_3 !== originalQuestion.choices.choice_3 ||
      currentQuestion.choices.choice_4 !== originalQuestion.choices.choice_4 ||
      currentQuestion.answer !== originalQuestion.answer

    if (questionChanged) {
      updatedQuestions += 1
    }

    // Track whether any answer key changed (triggers regrade warning text).
    if (currentQuestion.answer !== originalQuestion.answer) {
      answerUpdated = true
    }
  })

  return {
    updatedQuestions,
    answerUpdated
  }
}

export const hasMeaningfulHtmlContent = (html: string) => {
  /**
   * CMS rich-text fields are allowed to be image-only. We must treat img tags as
   * authored content before stripping markup, otherwise the empty-html checks would
   * incorrectly block question text or choices that intentionally contain only media.
   */
  const hasImageTag = /<img\b[^>]*>/i.test(html)
  const normalized =
    html
      ?.replace(/<p>(\s|&nbsp;|<br\s*\/?>)*<\/p>/gi, '')
      .replace(/<br\s*\/?>/gi, '')
      .replace(/<[^>]*>/g, '')
      .replace(/&nbsp;/gi, ' ')
      .trim() || ''

  return hasImageTag || normalized.length > 0
}

export const isChoiceEmpty = (html: string) => {
  return !hasMeaningfulHtmlContent(html)
}

export function getPageLabel(page: PageLike) {
  const pageType = getPageType(page)

  switch (pageType) {
    case PageLikeType.SECTION:
      return 'Section'
    case PageLikeType.QUESTION_SET:
      return 'Question Set'
    case PageLikeType.TEACHER_GUIDE_SECTION:
      return 'Teacher Guide Section'
    default:
      return 'Page'
  }
}
